import { useTranslation } from "react-i18next";
import AppInput from "../forms/AppInput";
import { useForm } from "react-hook-form";
import { useState, useEffect, useRef } from "react";
import { classNames } from "../../helpers";

import InputErrors from "../forms/InputErrors";
import PhoneNumberInput from "../forms/phone-input/PhoneNumberInput";
import { getTimezone } from "../../helpers/date_helpers";
import DropDown from "../forms/dropdown/DropDown";
import LogoWithLanguage from "../logo-with-language/LogoWithLanguage";
import { useTelawah } from "../../hooks/useTelawah";
import { useNavigate, useLocation } from "react-router-dom";
import { useNavigationHelpers } from "../../hooks/useNavigationHelpers";
import { ApiResponse } from "../../api/apiResponse";
import { createLearner } from "../../api/endpoints/learner/learner";
import toast from "react-hot-toast";

interface BookingFormData {
  firstname: string;
  middlename: string;
  lastname: string;
  mobile: string;
  sex: string;
  email: string;
  telawahId: string;
  program: string;
}

const NAME_REGEX_PATTERN = /^(([a-zA-Zء-ي])+\s*)+$/;

export default function BookingCandidateForm() {
  const LocalBasePath = "page.accountInfo";
  const { telawahOptions = [] } = useTelawah();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });

  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [programsList, setProgramsList] = useState([
    {
      key: t("khetma"),
      value: "khetma",
    },
    {
      key: t("ejaza"),
      value: "ejaza",
    },
  ]);
  const { handleBack } = useNavigationHelpers();

  const initialValues: BookingFormData = {
    firstname: "",
    middlename: "",
    lastname: "",
    mobile: "",
    sex: "",
    email: "",
    telawahId: "",
    program: "",
  };

  const getFormDataFromURL = (): BookingFormData => {
    const params = new URLSearchParams(location.search);
    const urlData: BookingFormData = { ...initialValues };

    if (params.has("first_name"))
      urlData.firstname = params.get("first_name") || "";
    if (params.has("middle_name"))
      urlData.middlename = params.get("middle_name") || "";
    if (params.has("last_name"))
      urlData.lastname = params.get("last_name") || "";
    if (params.has("phone_number"))
      urlData.mobile = params.get("phone_number") || "";
    if (params.has("gender")) urlData.sex = params.get("gender") || "";
    if (params.has("email")) urlData.email = params.get("email") || "";
    if (params.has("telawah_id"))
      urlData.telawahId = params.get("telawah_id") || "";
    if (params.has("program_name"))
      urlData.program = params.get("program_name") || "";

    return urlData;
  };

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<BookingFormData>({
    defaultValues: getFormDataFromURL(),
  });

  // Watch form values for URL synchronization
  const watchedValues = watch();
  const isResettingRef = useRef(false);
  const isUpdatingURLRef = useRef(false);

  useEffect(() => {
    // Only reset if this is external navigation, not our own URL update
    if (isUpdatingURLRef.current) {
      isUpdatingURLRef.current = false;
      return;
    }

    const formData = getFormDataFromURL();
    isResettingRef.current = true;
    reset(formData);
    setTimeout(() => {
      isResettingRef.current = false;
    }, 100);
  }, [location.search, reset]);

  const updateURLWithFormData = (data: BookingFormData) => {
    const params = new URLSearchParams();

    if (data.firstname) params.set("first_name", data.firstname);
    if (data.middlename) params.set("middle_name", data.middlename);
    if (data.lastname) params.set("last_name", data.lastname);
    if (data.mobile) params.set("phone_number", data.mobile);
    if (data.sex) params.set("gender", data.sex);
    if (data.email) params.set("email", data.email);
    if (data.telawahId) params.set("telawah_id", data.telawahId);
    if (data.program) params.set("program_name", data.program);

    const newSearch = params.toString();
    const currentSearch = location.search.replace("?", "");

    if (newSearch !== currentSearch) {
      isUpdatingURLRef.current = true; // Mark that we're updating URL
      const newURL = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;
      navigate(newURL, { replace: true });
    }
  };

  // Immediate URL update when form values change
  useEffect(() => {
    if (isResettingRef.current) return; // Skip URL update during reset

    // Only update URL if form has some data
    const hasData = Object.values(watchedValues).some(
      (value) => value && value.trim() !== ""
    );
    if (hasData) {
      updateURLWithFormData(watchedValues);
    }
  }, [watchedValues, location.pathname, navigate]); // Immediate updates, no debounce

  const saveData = async (data: any) => {
    let apiData: any = {
      full_name: `${data.firstname?.trim()} ${data.middlename?.trim()} ${data.lastname?.trim()}`,
      first_name: data.firstname?.trim(),
      middle_name: data.middlename?.trim(),
      last_name: data.lastname?.trim(),
      phone_number: data.mobile,
      gender: data.sex,
      program_name: data.program,
      telawah_id: data.telawahId,
      email: data.email,
      timezone: getTimezone(),
      submission: "",
    };
    // const queryParams = new URLSearchParams(apiData).toString();
    // navigate(`/learner/recitation-recording-panel?${queryParams}`);
    const response: ApiResponse = await createLearner(apiData);

    if (response.isSuccess) {
      toast?.success(t("savedSuccessfully"));

      const learnerToken = response.data?.learner_token;

      if (learnerToken) {
        navigate("/learner/success-booking-account", {
          replace: true,
          state: {
            redirectTo: `/learner/booking-calendar/${encodeURIComponent(
              learnerToken
            )}`,
          },
        });
        return;
      }

      navigate("/learner/success-booking-account", { replace: true });
    } else {
      response.errorMessages.forEach((errorMessage) => {
        toast?.error(errorMessage);
      });
    }
  };

  const onSubmit = (data: any) => {
    saveData(data);
  };

  return (
    <div className={classNames("w-full   flex flex-col md:bg-white  md:p-6 ")}>
      <div>
        <div className="flex flex-col items-center justify-between w-full mb-6 md:flex-row">
          <LogoWithLanguage />
        </div>
        <div className="px-4 pb-2 border-b border-gray-200 md:px-0">
          <h1 className="mb-2 text-3xl font-bold text-gray-700 ">
            {t("bookSession")}
          </h1>

          <p className="text-lg text-gray-500 ">{t("pleaseFillInfo")}</p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="pb-6 space-y-8 divide-y divide-gray-200"
      >
        <div className={classNames("px-4 md:px-0 pt-6")}>
          <div>
            <p className="text-lg font-medium leading-6 text-gray-800">
              {t("general")}
            </p>
          </div>
          <div className="pt-0">
            <label className={"mt-6 block text-sm font-medium text-gray-700"}>
              {t("name")} *
            </label>
            <div className="flex flex-col gap-y-5 gap-x-4 sm:flex-row">
              <div className="w-full max-w-lg sm:max-w-xs">
                <div className="mt-1">
                  <AppInput
                    hideLabel={true}
                    label={t("first")}
                    register={register}
                    error={errors.firstname}
                    scehema={{
                      required: true,
                      pattern: {
                        value: NAME_REGEX_PATTERN,
                        message: t("enterCharactersOnly"),
                      },
                    }}
                    placeholder={t("first")}
                    type="text"
                    name="firstname"
                    id="firstname"
                    autoComplete="given-name"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-brandblue-500 focus:border-brandblue-500 sm:text-sm"
                  />
                </div>
              </div>

              <div className="w-full max-w-lg sm:max-w-xs">
                <div className="mt-1">
                  <AppInput
                    hideLabel={true}
                    label={t("father")}
                    register={register}
                    error={errors.middlename}
                    scehema={{
                      required: true,
                      pattern: {
                        value: NAME_REGEX_PATTERN,
                        message: t("enterCharactersOnly"),
                      },
                    }}
                    placeholder={t("father")}
                    type="text"
                    name="middlename"
                    id="middlename"
                    autoComplete="middle-name"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-brandblue-500 focus:border-brandblue-500 sm:text-sm"
                  />
                </div>
              </div>
              <div className="w-full max-w-lg sm:max-w-xs">
                <div className="mt-1">
                  <AppInput
                    hideLabel={true}
                    label={t("family")}
                    register={register}
                    error={errors.lastname}
                    scehema={{
                      required: true,
                      pattern: {
                        value: NAME_REGEX_PATTERN,
                        message: t("enterCharactersOnly"),
                      },
                    }}
                    placeholder={t("family")}
                    type="text"
                    name="lastname"
                    id="lastname"
                    autoComplete="family-name"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-brandblue-500 focus:border-brandblue-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="sm:col-span-2">
            <div className="max-w-lg mt-4 sm:max-w-xs">
              <label
                htmlFor="gender"
                className="block mb-1 text-sm font-medium text-gray-700 sm:mt-px "
              >
                {t("gender")} *
              </label>
              <div className="flex gap-8">
                <div className="flex items-center">
                  <input
                    {...register("sex", { required: true })}
                    id="gender-male"
                    name="sex"
                    type="radio"
                    value={"male"}
                    className="w-4 h-4 border-gray-300 focus:ring-brandblue-500 text-brandblue-600"
                  />
                  <label
                    htmlFor="gender-male"
                    className="block text-sm font-medium text-gray-700 ltr:ml-3 rtl:mr-3"
                  >
                    {t("male")}
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    {...register("sex", { required: true })}
                    id="gender-female"
                    name="sex"
                    type="radio"
                    value={"female"}
                    className="w-4 h-4 border-gray-300 focus:ring-brandblue-500 text-brandblue-600"
                  />
                  <label
                    htmlFor="gender-female"
                    className="block text-sm font-medium text-gray-700 ltr:ml-3 rtl:mr-3"
                  >
                    {t("female")}
                  </label>
                </div>
              </div>
              {InputErrors(errors.sex, { required: true }, t("sex"), t("sex"))}
            </div>
          </div>
        </div>

        <div className="px-4 md:px-0">
          <div className="mt-8">
            <p className="text-lg font-medium leading-6 text-gray-800">
              {t("contactInfo")}
            </p>
          </div>
          <div className="sm:col-span-2">
            <div className="max-w-lg mt-6 sm:max-w-xs">
              <AppInput
                defaultValue={""}
                label={t("email")}
                register={register}
                error={errors.email}
                scehema={{
                  required: true,
                }}
                placeholder={t("email")}
                type="email"
                autoComplete="email"
                name="email"
                id="email"
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-brandblue-500 focus:border-brandblue-500 sm:text-sm"
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <div className="max-w-lg mt-6 sm:max-w-xs">
              <PhoneNumberInput
                control={control}
                label={t("mobile")}
                name={"mobile"}
                placeholder={"+1 234 567 8910"}
                schema={{ required: true }}
              />
            </div>
          </div>
        </div>

        <div className="px-4 md:px-0">
          <h3 className="pt-8 text-lg font-medium leading-6 text-gray-900">
            {t("programInfo")}
          </h3>
          <div className="sm:col-span-2">
            <div className="max-w-lg mt-6 sm:max-w-xs">
              <DropDown
                id="program"
                name="program"
                schema={{ required: true }}
                register={register}
                setValue={(value: any) =>
                  setValue("program", value, { shouldValidate: true })
                }
                defaultValue={`${getValues("program")}`}
                label={t("program")}
                placeholder={t("program")}
                error={!!errors.program}
                options={programsList}
              />
            </div>
          </div>

          <div className="sm:col-span-2">
            <div className="max-w-lg mt-6 sm:max-w-xs">
              <DropDown
                id="telawahId"
                name="telawahId"
                schema={{ required: true }}
                register={register}
                setValue={(value: any) =>
                  setValue("telawahId", value, { shouldValidate: true })
                }
                defaultValue={`${getValues("telawahId")}`}
                label={t("novel")}
                subLabel={t("allNovels")}
                placeholder={t("novel")}
                error={!!errors.telawahId}
                options={telawahOptions}
              />
            </div>
          </div>
        </div>

        <div
          className={classNames(
            "z-20 px-10 py-4 justify-end md:px-0 w-full sticky items-end  bottom-0 bg-white gap-3 flex "
          )}
        >
          <div className="flex items-center justify-between w-full gap-4">
            <button
              onClick={handleBack}
              className="flex-shrink-0 px-6 py-2 text-sm text-gray-700 border-2 border-gray-300 rounded-md whitespace-nowrap md:px-4"
            >
              {t("back")}
            </button>
            <button
              disabled={isUploading}
              type="submit"
              data-cy="save"
              className="flex justify-center flex-shrink-0 px-6 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm md:px-4 bg-brandblue-600 hover:bg-brandblue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500 disabled:bg-gray-400"
            >
              {t("save")}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
